import type { BaseField, FieldData } from '../fields/BaseField';
import type { OrderStrategy } from '../types/OrderStrategy';
import type { StrategyLike } from '../types/OrderStrategy.types';

/**
 * Filters parameter field keys based on both basic field visibility and conditional strategy parameter visibility.
 *
 * This function implements the common pattern used across multiple order forms where parameters are filtered by:
 * 1. Basic field visibility (`field.isVisible`)
 * 2. Conditional visibility based on strategy parameter ShowIfParamSet logic (`parameter.shouldBeVisible()`)
 *
 * @param parameterFields - Object containing parameter fields with visibility properties
 * @param strategy - The strategy containing parameter definitions with conditional visibility rules
 * @returns Array of parameter field keys that should be visible
 *
 * @example
 * ```typescript
 * const visibleParamKeys = filterVisibleParameterKeys(parameterFields, strategy);
 * const visibleParams = visibleParamKeys.map(key => parameterFields[key]);
 * ```
 */
export function filterVisibleParameterKeys(
  parameterFields: { [key: string]: BaseField<FieldData, any> },
  strategy: OrderStrategy | StrategyLike | undefined
): string[] {
  return Object.keys(parameterFields).filter(key => {
    // First check basic field visibility
    if (!parameterFields[key].isVisible) {
      return false;
    }

    // Then check conditional visibility based on ShowIfParamSet
    const parameter = strategy?.Parameters.find(p => p.Name === key);
    if (!parameter) {
      return true; // Show if parameter definition not found (e.g., LimitPrice for modifications)
    }

    // Use the parameter's shouldBeVisible method if it exists (OrderStrategy Parameter class)
    if ('shouldBeVisible' in parameter && typeof parameter.shouldBeVisible === 'function') {
      return parameter.shouldBeVisible(strategy as OrderStrategy, parameterFields);
    }

    // For StrategyLike parameters without shouldBeVisible method, just show them
    return true;
  });
}
